import React from 'react'
import { IntlShape, useIntl } from 'react-intl'
import styled from 'styled-components'
import { v4 as uuid } from 'uuid'

import { isStringNullOrEmpty, ucfirst } from '../../util/common'
import { toLocaleDateString } from '../../util/dateConversion'
import { HeaderAndDateToDate, HeaderAndMultipleValues, HeaderAndValue } from '../DisplayFields'
import { Section } from '../layout/section'
import { Heading4 } from '../layout/typography'
import { OwnersHolder, VehicleInfo } from './types'
import { TransferPermit } from '../../functions/graphqlApi/types/vehicleInfo'
import { Maybe } from '../../common/types'
import { messages } from './messages'

interface Props {
  vehicleInfo: VehicleInfo
  reFetch: () => void
}

const TrafiSearchContainer = styled.div`
  width: 100%;
  margin-bottom: 4rem;
`

const ColumnHeader = styled(Heading4 as any)`
  border-bottom: 2px solid lightgray;
  // margin-right: 1rem;
`

const TrafiSearch: React.FunctionComponent<Props> = ({ vehicleInfo }) => {
  const intl = useIntl()
  const { formatMessage } = intl
  const owners = vehicleInfo.ownersHolders || []
  const notKnown = formatMessage(messages.notKnown)

  return (
    <TrafiSearchContainer>
      <>
        <ColumnHeader>{formatMessage(messages.technicalDetailsHeader)}</ColumnHeader>
        <Section gap="2rem" justify="text" columns={3} tabletColumnCss="repeat( 2, minmax(200px,1fr) )">
          <HeaderAndValue header={formatMessage(messages.registrationNumber)} value={vehicleInfo.vehicle.registrationNumber || notKnown} />
          <HeaderAndValue header={formatMessage(messages.vehicleClass)} value={vehicleInfo.vehicle.vehicleClass || notKnown} />
          <HeaderAndValue header={formatMessage(messages.makeAndModel)} value={vehicleInfo.vehicle.makeAndModel || notKnown} />
          {vehicleInfo.vehicle.modelYear && (
            <HeaderAndValue header={formatMessage(messages.modelYear)} value={vehicleInfo.vehicle.modelYear} />
          )}
          <HeaderAndValue header={formatMessage(messages.serialNumber)} value={vehicleInfo.vehicle.serialNumber || notKnown} />
          <HeaderAndValue header={formatMessage(messages.vehicleStatus)} value={vehicleInfo.vehicle.vehicleStatus} />
          <HeaderAndValue header={formatMessage(messages.color)} value={vehicleInfo.vehicle.color || notKnown} />
          <HeaderAndValue header={formatMessage(messages.fuel)} value={vehicleInfo.vehicle.fuel || notKnown} />
          {vehicleInfo.vehicle.driveTrain && (
            <HeaderAndValue header={formatMessage(messages.driveTrain)} value={vehicleInfo.vehicle.driveTrain} />
          )}
          {vehicleInfo.vehicle.transmission && (
            <HeaderAndValue header={formatMessage(messages.transmission)} value={vehicleInfo.vehicle.transmission} />
          )}
          {vehicleInfo.vehicle.displacement && (
            <HeaderAndValue header={formatMessage(messages.displacement)} value={`${vehicleInfo.vehicle.displacement} cm³`} />
          )}
          {vehicleInfo.vehicle.maxNetEffect && (
            <HeaderAndValue header={formatMessage(messages.maxNetEffect)} value={`${vehicleInfo.vehicle.maxNetEffect} kW`} />
          )}
          {vehicleInfo.vehicle.weight && (
            <HeaderAndValue header={formatMessage(messages.weight)} value={`${vehicleInfo.vehicle.weight} kg`} />
          )}
          {vehicleInfo.vehicle.maxNetWeight && (
            <HeaderAndValue
              header={formatMessage(messages.maxNetWeight)}
              value={`${vehicleInfo.vehicle.maxNetWeight} kg`}
            />
          )}
          {vehicleInfo.vehicle.maxTechNetWeight && (
            <HeaderAndValue
              header={formatMessage(messages.maxTechNetWeight)}
              value={`${vehicleInfo.vehicle.maxTechNetWeight} kg`}
            />
          )}
          {vehicleInfo.vehicle.length && <HeaderAndValue header={formatMessage(messages.length)} value={`${vehicleInfo.vehicle.length} mm`} />}
          {vehicleInfo.vehicle.width && <HeaderAndValue header={formatMessage(messages.width)} value={`${vehicleInfo.vehicle.width} mm`} />}
          {vehicleInfo.vehicle.height && <HeaderAndValue header={formatMessage(messages.height)} value={`${vehicleInfo.vehicle.height} mm`} />}
        </Section>

        <ColumnHeader>{formatMessage(messages.registerAndInspectionHeader)}</ColumnHeader>
        <Section gap="2rem" justify="text" columns={3} tabletColumnCss="repeat( 2, minmax(200px,1fr) )">
          <HeaderAndValue header={formatMessage(messages.initializationDate)} value={vehicleInfo.vehicle.initializationDate || notKnown} />
          <HeaderAndValue
            header={formatMessage(messages.lastInspection)}
            value={`${ucfirst(vehicleInfo.vehicle.lastInspectionResultDescription || '')} ${toLocaleDateString(
              vehicleInfo.vehicle.lastInspectionDate
            )}`}
          />
          <HeaderAndValue header={formatMessage(messages.nextInspectionDate)} value={vehicleInfo.vehicle.nextInspectionDate || notKnown} />
          <HeaderAndValue header={formatMessage(messages.firstRegistrationDate)} value={vehicleInfo.vehicle.firstRegistrationDate || notKnown} />
        </Section>

        <ColumnHeader>{formatMessage(messages.insuranceInfoHeader)}</ColumnHeader>
        <Section gap="2rem" justify="text" columns={3} tabletColumnCss="repeat( 2, minmax(200px,1fr) )">
          <HeaderAndValue header={formatMessage(messages.insuranceCompany)} value={vehicleInfo.insurance.insuranceCompany || notKnown} />
          <HeaderAndDateToDate
            header={formatMessage(messages.insuranceStartAndEndDate)}
            startDate={vehicleInfo.insurance.insuranceStartDate}
            endDate={vehicleInfo.insurance.insuranceEndDate}
          />
          <HeaderAndValue header={formatMessage(messages.insuredParty)} value={vehicleInfo.insurance.insuredParty || notKnown} />
        </Section>

        {owners.map(o => {
          return (
            <div key={uuid()}>
              <ColumnHeader>{o.ownershipTypeDescription}</ColumnHeader>
              <Section gap="2rem" justify="text" columns={3} tabletColumnCss="repeat( 2, minmax(200px,1fr) )">
                <OwnersRow owner={o} intl={intl} />
              </Section>
            </div>
          )
        })}
        {vehicleInfo.transferPermit && (
          <>
            <ColumnHeader>{formatMessage(messages.transferPermitHeader)}</ColumnHeader>
            <Section gap="2rem" justify="text" columns={3} tabletColumnCss="repeat( 2, minmax(200px,1fr) )">
              <HeaderAndValue header={formatMessage(messages.purposeOfUse)} value={vehicleInfo.transferPermit.purposeOfUse} />
              <HeaderAndValue
                header={formatMessage(messages.validityPeriod)}
                value={vehicleInfo.transferPermit.validityStarts + ' - ' + vehicleInfo.transferPermit.validityEnds}
              />
              <HeaderAndValue header={formatMessage(messages.applicant)} value={applicantInfo(vehicleInfo.transferPermit, formatMessage)} />
              <HeaderAndValue header={formatMessage(messages.route)} value={vehicleInfo.transferPermit.route} />
            </Section>
          </>
        )}
        {(!isStringNullOrEmpty(vehicleInfo.vehicle.remarks) || vehicleInfo.restrictions.length > 0) && (
          <>
            {vehicleInfo.restrictions.length > 0 && (
              <>
                <ColumnHeader>{formatMessage(messages.restrictionsHeader)}</ColumnHeader>
                {vehicleInfo.restrictions.map((r, i) => {
                  return (
                    <div key={uuid()}>
                      <Section gap="2rem" justify="text" columns={2} tabletColumnCss="repeat( 2, minmax(200px,1fr) )">
                        <HeaderAndValue header={formatMessage(messages.restrictionType)} value={r.description} />
                        <HeaderAndValue
                          header={formatMessage(messages.restrictionSeverity)}
                          value={`${r.seriousnessLevel}, ${r.seriousnessDescription}`}
                        />
                      </Section>
                      {i < vehicleInfo.restrictions.length - 1 && <ColumnHeader />}
                    </div>
                  )
                })}
              </>
            )}
            {!isStringNullOrEmpty(vehicleInfo.vehicle.remarks) && (
              <>
                <ColumnHeader>{formatMessage(messages.remarksHeader)}</ColumnHeader>
                <Section gap="2rem" justify="text" columns={3} tabletColumnCss="repeat( 2, minmax(200px,1fr) )">
                  <HeaderAndValue header={formatMessage(messages.remarks)} value={vehicleInfo.vehicle.remarks} />
                </Section>
              </>
            )}
          </>
        )}

      </>
    </TrafiSearchContainer>
  )
}

function applicantInfo(transferPermit: Maybe<TransferPermit>, formatMessage: IntlShape['formatMessage']) {

  if ( !transferPermit || !transferPermit.applicant.firstNameSubUnit) {
    return formatMessage(messages.notAvailable)
  }

  const { lastNameMainUnit, firstNameSubUnit, streetAddress, postalCode, city } = transferPermit.applicant

  const name = `${lastNameMainUnit + ' ' + firstNameSubUnit}`
  const address = `${streetAddress + ', ' + postalCode + ' ' + city}`

  return `${name}, ${address}`
}

interface OwnersRowProps {
  owner: OwnersHolder
  intl: IntlShape
}
const OwnersRow = ({ owner, intl }: OwnersRowProps) => {
  const { formatMessage } = intl
  const { identifier, name, address, ownershipStartDate, ownershipEndDate } = getRowTexts(owner, formatMessage)

  return (
    <>
      <HeaderAndValue header={formatMessage(messages.applicantName)} value={name} />
      {owner.customer && owner.customer.type === 'company' && (
        <HeaderAndValue header={formatMessage(messages.businessId)} value={identifier} />
      )}
      <HeaderAndMultipleValues
        header={formatMessage(messages.address)}
        values={address}
      />
      {ownershipStartDate || ownershipEndDate ? (
        <HeaderAndDateToDate
          header={formatMessage(messages.ownershipStartAndEndDate)}
          startDate={ownershipStartDate}
          endDate={ownershipEndDate}
        />
      ) : (
        <HeaderAndValue
          header={formatMessage(messages.ownershipStartAndEndDate)}
          value={formatMessage(messages.notAvailable)}
        />
      )}
    </>
  )
}

export default TrafiSearch

function getRowTexts(owner: OwnersHolder, formatMessage: IntlShape['formatMessage']) {
  const customer = owner.customer;

  const emptyField = formatMessage(messages.notAvailable)
  const empty = formatMessage(messages.notKnown)

  const identifier = customer && customer.identifier ? customer.identifier : emptyField
  const name = customer && customer.name ? customer.name : emptyField

  const zipCode = owner.customer && owner.customer.zipcode ? owner.customer.zipcode : empty
  const city = owner.customer && owner.customer.city ? owner.customer.city : empty
  const customerAddress = owner.customer && owner.customer.address ? owner.customer.address : empty
  const address = [`${customerAddress}`, `${zipCode}, ${city}`]

  const ownershipStartDate = owner.ownershipStartDate ? owner.ownershipStartDate : '';
  const ownershipEndDate = owner.ownershipEndDate ? owner.ownershipEndDate : '';

  return { identifier, name, address, ownershipStartDate, ownershipEndDate }
}
